import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertTriangle, Building2, CheckCircle2, Landmark, Loader2, Search, UserCheck } from 'lucide-react'
import { Card, Badge, Button } from '@/components/ui'
import {
  listUnlinkedLegalEntities,
  setLegalEntityPerson,
  type UnlinkedLegalEntity,
} from '@/services/partyIdentityService'

// ============================================================================
// «Юрлица без владельца» — список для юристов, чтобы пройти непривязанные ИП
// одним заходом вместо того, чтобы натыкаться на них по одному в карточках ДКК.
//
// Ключевое здесь — подсказка из БАНКА: имя плательщика в выписке это факт на
// момент операции, в отличие от названия в системе, которое могло устареть или
// содержать опечатку (боевой пример: «Казакова Антонита Владимировна ИП» при
// банковском «КАЗАКОВА АНТОНИНА ВЛАДИМИРОВНА»).
//
// Модель и радиус — docs/PARTY_IDENTITY_CHANGE_PLAN.md.
// ============================================================================

type Filter = 'all' | 'ready' | 'needs_review' | 'ip_only'

function errorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message
  return fallback
}

function fmtDate(value: string | null): string {
  if (!value) return '—'
  const date = new Date(`${value}T00:00:00`)
  if (Number.isNaN(date.getTime())) return value
  return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const KIND_LABELS: Record<UnlinkedLegalEntity['entity_kind'], string> = {
  IP: 'ИП',
  ORG: 'организация',
  NO_INN: 'без ИНН',
  OTHER: 'ИНН нестандартный',
}

export function LegalEntityOwnersPage() {
  const [rows, setRows] = useState<UnlinkedLegalEntity[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [done, setDone] = useState<Record<number, string>>({})
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<Filter>('all')

  const load = useCallback(() => {
    setLoading(true)
    listUnlinkedLegalEntities()
      .then(data => {
        setRows(data)
        setError(null)
      })
      .catch(loadError => setError(errorMessage(loadError, 'Не удалось загрузить список')))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => load(), [load])

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase()
    return rows.filter(row => {
      if (filter === 'ready' && !row.lastname_matches_person) return false
      if (filter === 'needs_review' && row.lastname_matches_person) return false
      if (filter === 'ip_only' && row.entity_kind !== 'IP') return false
      if (!q) return true
      return [
        row.legal_name,
        row.inn,
        row.partner_number,
        row.bank_last_payer_name,
        ...row.partner_persons.map(p => `${p.last_name} ${p.first_name}`),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(q)
    })
  }, [rows, query, filter])

  const counts = useMemo(
    () => ({
      total: rows.length,
      ready: rows.filter(r => r.lastname_matches_person).length,
      review: rows.filter(r => !r.lastname_matches_person).length,
      withBank: rows.filter(r => r.bank_last_payer_name).length,
    }),
    [rows]
  )

  async function handleAssign(entityId: number, personId: number, label: string) {
    setSavingId(entityId)
    setError(null)
    try {
      await setLegalEntityPerson({ entityId, personId })
      setDone(previous => ({ ...previous, [entityId]: label }))
      setRows(previous => previous.filter(row => row.entity_id !== entityId))
    } catch (assignError: unknown) {
      setError(errorMessage(assignError, 'Не удалось привязать владельца'))
    } finally {
      setSavingId(null)
    }
  }

  return (
    <div className="space-y-4 p-4">
      <div>
        <h1 className="flex items-center gap-2 text-lg font-semibold">
          <Landmark size={18} />
          Юрлица без владельца
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Для каждого юрлица нужно указать, на кого оно оформлено. Пока владелец не проставлен,
          смена фамилии не может поменять название ИП связанно — юристу приходится править вручную.
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Организациям (ООО, ТОО) владелец не нужен — их можно оставить как есть. Смысл имеет
          только для ИП, где в названии стоит ФИО человека.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertTriangle size={18} className="mt-0.5 shrink-0" />
          <div>{error}</div>
        </div>
      )}

      {Object.keys(done).length > 0 && (
        <div className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-700">
          <CheckCircle2 size={18} className="mt-0.5 shrink-0" />
          <div>Привязано за этот заход: {Object.keys(done).length}.</div>
        </div>
      )}

      <Card>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex h-9 min-w-[240px] flex-1 items-center gap-2 rounded-md border border-border bg-background px-2 focus-within:border-primary">
            <Search size={15} className="text-muted-foreground" />
            <input
              value={query}
              onChange={event => setQuery(event.target.value)}
              className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
              placeholder="название, ИНН, франчайзи, ФИО, имя из банка"
            />
          </div>
          <FilterButton active={filter === 'all'} onClick={() => setFilter('all')}>
            Все · {counts.total}
          </FilterButton>
          <FilterButton active={filter === 'ready'} onClick={() => setFilter('ready')}>
            Фамилия совпадает · {counts.ready}
          </FilterButton>
          <FilterButton active={filter === 'needs_review'} onClick={() => setFilter('needs_review')}>
            Требует решения · {counts.review}
          </FilterButton>
          <FilterButton active={filter === 'ip_only'} onClick={() => setFilter('ip_only')}>
            Только ИП
          </FilterButton>
        </div>
        <div className="mt-2 text-xs text-muted-foreground">
          Подсказка из банка есть у {counts.withBank} из {counts.total}. «Фамилия совпадает» — тот,
          у кого фамилия участника уже стоит в названии: такие закрываются одним кликом.
        </div>
      </Card>

      {loading ? (
        <div className="flex h-40 items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 size={16} className="animate-spin" />
          Загрузка
        </div>
      ) : visible.length === 0 ? (
        <Card>
          <div className="py-8 text-center text-sm text-muted-foreground">
            {rows.length === 0
              ? 'Все юрлица имеют владельца.'
              : 'Под текущий фильтр ничего не подходит.'}
          </div>
        </Card>
      ) : (
        <div className="space-y-2">
          {visible.map(row => (
            <Card key={row.entity_id}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Building2 size={15} className="shrink-0 text-muted-foreground" />
                    <span className="min-w-0 break-words">{row.legal_name}</span>
                    <Badge variant="outline">{KIND_LABELS[row.entity_kind]}</Badge>
                    {row.is_primary && <Badge variant="outline">основное</Badge>}
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    Франчайзи № {row.partner_number} · ИНН {row.inn ?? '—'}
                  </div>

                  {row.bank_last_payer_name ? (
                    <div className="mt-2 rounded-md border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900">
                      <span className="font-medium">В банке платит как:</span>{' '}
                      {row.bank_last_payer_name}
                      <span className="text-blue-800">
                        {' '}
                        · последний платёж {fmtDate(row.bank_last_payment_date)} · всего{' '}
                        {row.bank_payment_count}
                      </span>
                    </div>
                  ) : (
                    <div className="mt-2 text-xs text-muted-foreground">
                      Платежей по этому ИНН нет — подсказать из банка нечем.
                    </div>
                  )}
                </div>

                <div className="min-w-[260px] space-y-1">
                  <div className="text-xs font-medium text-foreground">Кто из участников это?</div>
                  {row.partner_persons.length === 0 ? (
                    <div className="text-xs text-muted-foreground">
                      У франчайзи нет активных участников — сначала заведите персону.
                    </div>
                  ) : (
                    row.partner_persons.map(person => (
                      <div key={person.person_id} className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate text-xs">
                          {person.last_name} {person.first_name}
                          {person.lastname_in_name && (
                            <span className="ml-1 text-green-700">· фамилия в названии</span>
                          )}
                        </span>
                        <Button
                          size="sm"
                          variant={person.lastname_in_name ? 'primary' : 'outline'}
                          disabled={savingId === row.entity_id}
                          onClick={() =>
                            handleAssign(
                              row.entity_id,
                              person.person_id,
                              `${person.last_name} ${person.first_name}`
                            )
                          }
                        >
                          {savingId === row.entity_id ? (
                            <Loader2 size={13} className="animate-spin" />
                          ) : (
                            <UserCheck size={13} />
                          )}
                        </Button>
                      </div>
                    ))
                  )}
                  {!row.lastname_matches_person && row.partner_persons.length > 0 && (
                    <div className="mt-1 rounded-md border border-amber-200 bg-amber-50 p-2 text-[11px] text-amber-800">
                      Ни одна фамилия участника не совпадает с названием. Это либо формальный
                      представитель, либо фамилия давно сменилась, либо в группе указан не тот
                      человек — сверьтесь с банком выше, прежде чем привязывать.
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function FilterButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-md border px-2 py-1.5 text-xs ${
        active
          ? 'border-primary bg-primary/10 text-foreground'
          : 'border-border text-muted-foreground hover:bg-accent'
      }`}
    >
      {children}
    </button>
  )
}

